/**
 * Reinigungs-Kalender-Helpers — reine Funktionen auf rohen yyyy-mm-dd-Daten, ohne Verschiebung
 * durch die Zeitzone des Browsers.
 */

/** UTC-Midnight-Date aus einem yyyy-mm-dd-String — stabile Wochen-Arithmetik ohne TZ-Drift. */
function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

export function addDaysIso(iso: string, days: number): string {
  const d = parseIsoDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Montag der Woche, die `iso` enthält (ISO-Woche, Mo=Start). */
export function startOfWeekIso(iso: string): string {
  const d = parseIsoDate(iso);
  const dow = d.getUTCDay(); // 0=So..6=Sa
  const deltaToMonday = dow === 0 ? -6 : 1 - dow;
  return addDaysIso(iso, deltaToMonday);
}

/** Die 7 Tage (yyyy-mm-dd) ab Wochenstart. */
export function weekDays(startIso: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysIso(startIso, i));
}

export function todayInTimeZone(timeZone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const value = (type: 'year' | 'month' | 'day') =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

export function parseCleaningDate(value: unknown, fallback: string): string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return fallback;
  return Number.isNaN(Date.parse(`${value}T00:00:00Z`)) ? fallback : value;
}

const WEEKDAY_SHORT_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const MONTH_SHORT_DE = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];

/**
 * Wochen-Label aus rohen yyyy-mm-dd (kein TZ-Shift). Über Monatsgrenzen wird der Monat
 * an beiden Enden genannt ("27. Jul – 2. Aug"), sonst kompakt ("13.–19. Jul").
 */
export function weekRangeLabel(startIso: string, endIso: string): string {
  const startMonth = MONTH_SHORT_DE[Number(startIso.slice(5, 7)) - 1] ?? '';
  const endMonth = MONTH_SHORT_DE[Number(endIso.slice(5, 7)) - 1] ?? '';
  const startDay = Number(startIso.slice(8, 10));
  const endDay = Number(endIso.slice(8, 10));
  return startMonth === endMonth
    ? `${startDay}.–${endDay}. ${endMonth}`
    : `${startDay}. ${startMonth} – ${endDay}. ${endMonth}`;
}

/** "2026-07-13" → { weekday: "Mo", day: "13" } für Header/Strip. */
export function dayLabel(iso: string): { weekday: string; day: string } {
  const d = parseIsoDate(iso);
  return { weekday: WEEKDAY_SHORT_DE[d.getUTCDay()] ?? '', day: String(d.getUTCDate()) };
}
